import React from 'react';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import CodeIcon from '@mui/icons-material/Code';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Editor, EditorContent } from '@tiptap/react';
import VeraPromptContextGuide from '../VeraPromptContextGuide';
import { getVeraPromptContextGuide } from '../veraPromptContextGuides';
import { VeraPromptEditorToolbar } from '../veraPromptEditorUtils';
import { StyledLabel } from '../../styles/styles';
import { MONOSPACE_TEXTAREA_STYLE } from './adminAssistPromptStyles';

interface AdminAssistCoachingPromptTabProps {
  markdown: string;
  productionMarkdown: string;
  rawMode: boolean;
  onRawModeChange: (raw: boolean) => void;
  onMarkdownChange: (value: string) => void;
  onRawBlur: () => void;
  editor: Editor | null;
}

export default function AdminAssistCoachingPromptTab({
  markdown,
  productionMarkdown,
  rawMode,
  onRawModeChange,
  onMarkdownChange,
  onRawBlur,
  editor,
}: AdminAssistCoachingPromptTabProps) {
  return (
    <>
      <VeraPromptContextGuide markdown={getVeraPromptContextGuide('admin_coaching')} />
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <StyledLabel stacked>Proposed Markdown</StyledLabel>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={rawMode ? 'raw' : 'rich'}
            onChange={(_, v) => v && onRawModeChange(v === 'raw')}
          >
            <ToggleButton value="rich">
              <AutoAwesome sx={{ fontSize: 16, mr: 0.5 }} />
              Rich
            </ToggleButton>
            <ToggleButton value="raw">
              <CodeIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Raw
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {rawMode ? (
          <textarea
            value={markdown}
            onChange={(event) => onMarkdownChange(event.target.value)}
            onBlur={onRawBlur}
            rows={20}
            style={MONOSPACE_TEXTAREA_STYLE}
          />
        ) : (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              cursor: 'text',
              '& .ProseMirror': { outline: 'none', minHeight: 240, p: 2 },
              '& .ProseMirror h1': { fontSize: '1.6em', fontWeight: 700, mt: 0 },
              '& .ProseMirror h2': { fontSize: '1.3em', fontWeight: 700 },
              '& .ProseMirror h3': { fontSize: '1.1em', fontWeight: 700 },
              '& .ProseMirror code': {
                background: '#f3f4f6',
                px: 0.5,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: 13,
              },
              '& .ProseMirror pre': {
                background: '#f3f4f6',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
              },
              '& .ProseMirror pre code': { background: 'none', p: 0 },
              '& .ProseMirror blockquote': {
                borderLeft: '3px solid #ccc',
                pl: 2,
                color: 'text.secondary',
              },
              '& .ProseMirror ul': { pl: 3 },
              '& .ProseMirror ol': { pl: 3 },
              '& .ProseMirror hr': { border: 'none', borderTop: '2px solid #e0e0e0', my: 2 },
            }}
            onClick={() => editor?.commands.focus()}
          >
            <VeraPromptEditorToolbar editor={editor} />
            <EditorContent editor={editor} />
          </Box>
        )}
      </Box>

      {productionMarkdown && (
        <Box sx={{ mb: 3 }}>
          <StyledLabel stacked>Current coded version (read-only reference)</StyledLabel>
          <Box
            component="pre"
            sx={{
              mt: 1,
              p: 1.5,
              background: '#f5f5f5',
              borderRadius: 1,
              fontSize: '0.78rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              border: '1px solid',
              borderColor: 'divider',
              fontFamily: 'monospace',
              maxHeight: 180,
              overflowY: 'auto',
            }}
          >
            {productionMarkdown}
          </Box>
        </Box>
      )}
    </>
  );
}
